/**
 * ADR-071 — admin alerting API client (portal-staff).
 *
 * Wraps the admin-api alert routes. The types mirror the lambda `Alert` model
 * (lambdas/shared/src/alerts.ts) — the frontend keeps its own copy per the
 * backend/frontend split (same pattern as the other admin lib modules).
 */
import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, zoneByCode, type PortalZone } from './zones';

// `security` (ADR-080 §5) — the SOW-perimeter producer; new category, existing
// pipe. Kept in sync with the shared lambdas/shared/src/alerts.ts AlertSource
// (§9 copy-fork of the alert-model types for the portal UI).
export type AlertSource =
  | 'scoring' | 'vendor' | 'deploy' | 'auth' | 'snippet' | 'email' | 'security' | 'signup'
  | 'liveness' | 'billing';
export type AlertSeverity = 'info' | 'warning' | 'critical';
/** `auto_cleared` (ADR-071 AM3, Jeff 30 October 2026): recovered by itself, still on the board
 *  until an admin acknowledges it; counted as neither critical, warning nor info. */
export type AlertStatus = 'active' | 'acknowledged' | 'auto_cleared' | 'cleared';

export interface AlertEventSample {
  ts: string;
  detail: string;
}

export interface Alert {
  alertId: string;
  source: AlertSource;
  type: string;
  subjectClass: string;
  severity: AlertSeverity;
  subject: string;
  summary: string;
  detail?: string;
  firstSeen: string;
  lastSeen: string;
  count: number;
  status: AlertStatus;
  flapping?: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  clearedAt?: string;
  sampleEvents?: AlertEventSample[];
  /**
   * O1 fix 3 (11 October 2026) — a PLATFORM-WIDE alert (one global service, e.g. the sow-gate
   * Worker): raised once by one zone's evaluator, shown as "All zones" under every zone filter
   * and counted once. `zone` still says which zone's table holds it, so actions route there.
   */
  scope?: 'platform';

  /**
   * ADR-120 P8 step 7(b) — the zone this alert fired in. Added by the client after the
   * fan-out; no zone's API names itself. Always populated for an alert from `listAlerts`,
   * and it is what `actOnAlert` routes the transition back to.
   */
  zone?: string;
}

export interface SeverityCounts {
  critical: number;
  warning: number;
  info: number;
}

export interface AlertSummary {
  total: SeverityCounts;
  bySource: Record<AlertSource, SeverityCounts>;
  acknowledged: number;
  navbarCritical: number;
  /** AM3 — absent from an API that predates it. */
  autoCleared?: number;
}

export type AlertAction = 'acknowledge' | 'unacknowledge' | 'clear' | 'dismiss';

/**
 * The board's counts, from the alert LIST (quality register Q1, 30 October 2026).
 *
 * ⭐ THE NAVBAR, THE CRITICAL CARD AND THE CRITICAL PILL ALL TAKE `critical` FROM HERE. They used
 * to come from two endpoints read at different times (the navbar from `/admin/alerts/summary`
 * every 60 s, the page from `/admin/alerts` once), and on 30 October they disagreed — 1 against
 * 2. One function over one list cannot disagree with itself.
 *
 * Status before severity: an acknowledged alert counts only as acknowledged, an auto-cleared one
 * only as auto-cleared (AM3) — so `critical` is exactly the active, unacknowledged criticals the
 * navbar lights on (A1).
 */
export interface BoardCounts {
  /** Every row on the board. */
  all: number;
  critical: number;
  warning: number;
  info: number;
  acknowledged: number;
  autoCleared: number;
}

export function boardCounts(alerts: ReadonlyArray<Pick<Alert, 'severity' | 'status'>>): BoardCounts {
  const c: BoardCounts = { all: 0, critical: 0, warning: 0, info: 0, acknowledged: 0, autoCleared: 0 };
  for (const a of alerts) {
    if (a.status === 'cleared') continue;
    c.all += 1;
    if (a.status === 'acknowledged') c.acknowledged += 1;
    else if (a.status === 'auto_cleared') c.autoCleared += 1;
    else c[a.severity] += 1;
  }
  return c;
}

/**
 * Per-source open counts (the Home widget, A2) from the same list. Open = active or acknowledged,
 * counted by severity — the widget's long-standing meaning ("accrues"); an auto-cleared alert is
 * over and is not counted.
 */
export function summariseBoard(alerts: ReadonlyArray<Pick<Alert, 'severity' | 'status' | 'source'>>): AlertSummary {
  const bySource = {} as Record<AlertSource, SeverityCounts>;
  let total: SeverityCounts = { ...ZERO };
  let acknowledged = 0;
  let navbarCritical = 0;
  let autoCleared = 0;
  for (const a of alerts) {
    if (a.status === 'auto_cleared') {
      autoCleared += 1;
      continue;
    }
    if (a.status !== 'active' && a.status !== 'acknowledged') continue;
    const one = { ...ZERO, [a.severity]: 1 };
    total = addCounts(total, one);
    bySource[a.source] = addCounts(bySource[a.source] ?? ZERO, one);
    if (a.status === 'acknowledged') acknowledged += 1;
    else if (a.severity === 'critical') navbarCritical += 1;
  }
  return { total, bySource, acknowledged, navbarCritical, autoCleared };
}

const ZERO: SeverityCounts = { critical: 0, warning: 0, info: 0 };
const addCounts = (a: SeverityCounts, b: SeverityCounts): SeverityCounts => ({
  critical: a.critical + b.critical,
  warning: a.warning + b.warning,
  info: a.info + b.info,
});

/**
 * Sum per-zone summaries into one.
 *
 * Kept for `listAlerts`'s `summary` field; the console's own counts come from `boardCounts` /
 * `summariseBoard` over the list, so an API that predates a field cannot skew them.
 *
 * ⚠ THE COUNTS MUST BE ADDED, NOT TAKEN FROM THE HOME ZONE. The navbar's critical indicator
 * reads `navbarCritical`; if it kept showing only AU's count, a critical alert in us-east-1
 * would fire, sit in the list, and never light the indicator an operator actually watches —
 * an alerting gap that looks exactly like silence.
 */
function mergeSummaries(parts: AlertSummary[]): AlertSummary {
  const bySource = {} as Record<AlertSource, SeverityCounts>;
  for (const p of parts) {
    for (const [src, counts] of Object.entries(p.bySource) as [AlertSource, SeverityCounts][]) {
      bySource[src] = addCounts(bySource[src] ?? ZERO, counts);
    }
  }
  return {
    total: parts.reduce((acc, p) => addCounts(acc, p.total), ZERO),
    bySource,
    acknowledged: parts.reduce((n, p) => n + p.acknowledged, 0),
    navbarCritical: parts.reduce((n, p) => n + p.navbarCritical, 0),
    autoCleared: parts.reduce((n, p) => n + (p.autoCleared ?? 0), 0),
  };
}

export interface AlertListResult {
  alerts: Alert[];
  summary: AlertSummary;
  /** Labels of zones that did not answer — see `lib/zones.ts`; render this, never drop it. */
  failedZones: string[];
}

/**
 * The detail-view list (open alerts, critical-first) plus the summary, across every zone
 * (ADR-120 P8 step 7(b)). Fanned out from this browser, one request per zone.
 */
export async function listAlerts(): Promise<AlertListResult> {
  const summaries = new Map<string, AlertSummary>();
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await adminApiInZone<{ alerts: Alert[]; summary: AlertSummary }>(zone, '/admin/alerts');
    summaries.set(zone.code, r.summary);
    return r.alerts.map((a) => ({ ...a, zone: zone.code }));
  });
  const severityRank: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
  return {
    // Critical-first across the merged set — a US critical must not sort below an AU info
    // just because AU's zone was read first.
    alerts: results
      .flatMap((r) => r.items)
      .sort((a, b) =>
        severityRank[a.severity] - severityRank[b.severity] ||
        b.lastSeen.localeCompare(a.lastSeen),
      ),
    summary: mergeSummaries([...summaries.values()]),
    failedZones: failedZoneLabels(results),
  };
}

/**
 * Single-row operator transition (acknowledge / unacknowledge / clear).
 *
 * ⚠ TAKES THE ALERT, NOT JUST ITS ID, AND THAT IS THE POINT. An alert only exists in the zone
 * it fired in. Sending an acknowledge for a us-east-1 alert to ap-southeast-2's API finds
 * nothing — the operator clicks, the row does not change, and there is no error that explains
 * why. Routing the transition to `alert.zone` is what makes the button mean what it says.
 */
export function actOnAlert(
  alert: Pick<Alert, 'alertId' | 'zone'>,
  action: 'acknowledge' | 'unacknowledge' | 'clear',
): Promise<{ alert: Alert }> {
  const zone = zoneByCode(alert.zone);
  const path = `/admin/alerts/${encodeURIComponent(alert.alertId)}/${action}`;
  return zone ? adminApiInZone<{ alert: Alert }>(zone, path, { method: 'POST' })
              : adminApi<{ alert: Alert }>(path, { method: 'POST' });
}

/** Bulk operator transition over many rows (A4 bulk actions). */
/**
 * Bulk operator transition over many rows (A4 bulk actions).
 *
 * ⚠ ONE REQUEST PER ZONE, NOT ONE REQUEST. A selection made on the merged list can span
 * jurisdictions, and each zone's bulk route can only find its own alerts. Sending the whole
 * set to one zone would report every other zone's ids as `missing` — which reads as data loss
 * rather than as a misrouted call.
 */
export async function bulkActOnAlerts(
  action: AlertAction,
  alerts: Pick<Alert, 'alertId' | 'zone'>[],
): Promise<{ action: AlertAction; updated: Alert[]; missing: string[] }> {
  const byZone = new Map<PortalZone, string[]>();
  for (const a of alerts) {
    const zone = zoneByCode(a.zone);
    if (!zone) continue;
    byZone.set(zone, [...(byZone.get(zone) ?? []), a.alertId]);
  }
  const parts = await Promise.all(
    [...byZone.entries()].map(([zone, alertIds]) =>
      adminApiInZone<{ action: AlertAction; updated: Alert[]; missing: string[] }>(
        zone,
        '/admin/alerts/bulk',
        { method: 'POST', body: JSON.stringify({ action, alertIds }) },
      ).then((r) => ({ ...r, updated: r.updated.map((u) => ({ ...u, zone: zone.code })) })),
    ),
  );
  return {
    action,
    updated: parts.flatMap((p) => p.updated),
    missing: parts.flatMap((p) => p.missing),
  };
}
